import { AlertCircle } from "lucide-react";
import { buildCalendarCells, parseWorkDate } from "../../Moduels/attendance/attendanceCalendarUtils";
import { formatMonthYearIndian, isTodayInMonth } from "../../Moduels/attendance/indianCalendarUtils";
import "../../styles/IndianCalendar.css";

function monthsInRange(fromDate, toDate) {
  if (!fromDate || !toDate) return [];
  const [fy, fm] = fromDate.split("-").map(Number);
  const [ty, tm] = toDate.split("-").map(Number);
  const months = [];
  let y = fy;
  let m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    months.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return months;
}

function buildExceptionDayMap(rows, monthKey) {
  const map = {};
  for (const r of rows || []) {
    const parts = parseWorkDate(r.work_date);
    if (!parts || parts.monthKey !== monthKey) continue;
    map[parts.day] = (map[parts.day] || 0) + 1;
  }
  return map;
}

function isDayInRange(isoDate, fromDate, toDate) {
  return isoDate >= fromDate && isoDate <= toDate;
}

function MonthGrid({ monthKey, rows, fromDate, toDate, selectedDate, onDaySelect }) {
  const { cells, weekdayHeaders } = buildCalendarCells(monthKey);
  const dayMap = buildExceptionDayMap(rows, monthKey);
  const monthMeta = formatMonthYearIndian(monthKey);

  return (
    <div className="ind-cal-grid-wrap">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-rose-50/40">
        <p className="text-sm font-bold text-slate-800">{monthMeta.label}</p>
      </div>
      <div className="p-3">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekdayHeaders.map((name) => (
            <div
              key={name}
              className={`ind-cal-weekday ${
                name === "Saturday" ? "sat" : name === "Sunday" ? "sun" : "weekday"
              }`}
            >
              <span className="hidden sm:inline">{name.slice(0, 3)}</span>
              <span className="sm:hidden">{name.slice(0, 1)}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell) => {
            if (cell.empty) {
              return <div key={cell.key} className="min-h-[3.25rem]" aria-hidden />;
            }
            const iso = `${monthKey}-${String(cell.day).padStart(2, "0")}`;
            const inRange = isDayInRange(iso, fromDate, toDate);
            const count = dayMap[cell.day] || 0;
            const hasMissing = count > 0;
            const isSelected = selectedDate === iso;
            const isToday = isTodayInMonth(monthKey, cell.day);

            return (
              <button
                key={cell.key}
                type="button"
                disabled={!inRange}
                onClick={() => inRange && onDaySelect?.(iso)}
                className={`min-h-[3.25rem] rounded-lg border text-left p-1.5 transition-colors ${
                  !inRange
                    ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
                    : hasMissing
                      ? "bg-rose-50 border-rose-300 hover:border-rose-400 cursor-pointer"
                      : "bg-white border-slate-200 hover:border-teal-300 cursor-pointer"
                } ${isSelected ? "ring-2 ring-teal-500 border-teal-500" : ""} ${
                  isToday && inRange ? "shadow-sm" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-0.5">
                  <span
                    className={`text-xs font-semibold ${
                      isToday && inRange ? "text-teal-700" : inRange ? "text-slate-700" : "text-slate-300"
                    }`}
                  >
                    {cell.day}
                  </span>
                  {hasMissing ? (
                    <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-rose-600 text-white text-[9px] font-bold leading-none">
                      <AlertCircle className="w-2.5 h-2.5" />
                      {count}
                    </span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MissingPunchCalendar({
  fromDate,
  toDate,
  rows = [],
  selectedDate,
  onDaySelect,
}) {
  const months = monthsInRange(fromDate, toDate);
  const totalMissing = (rows || []).length;

  if (!fromDate || !toDate) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
        <p>
          <span className="font-semibold text-slate-800">{totalMissing}</span> missing punch
          {totalMissing === 1 ? "" : "es"} in selected range
        </p>
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-800">
            <span className="w-2 h-2 rounded-full bg-rose-500" />
            Day with missing punch
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600">
            <span className="w-2 h-2 rounded-full bg-slate-200" />
            No issues
          </span>
        </div>
      </div>
      <div
        className={`grid gap-4 ${
          months.length > 1 ? "md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 max-w-xl"
        }`}
      >
        {months.map((monthKey) => (
          <MonthGrid
            key={monthKey}
            monthKey={monthKey}
            rows={rows}
            fromDate={fromDate}
            toDate={toDate}
            selectedDate={selectedDate}
            onDaySelect={onDaySelect}
          />
        ))}
      </div>
    </div>
  );
}

export { monthsInRange };
